/**
 * DOM-free resolution of a renderer mark's KIND from its wire type +
 * attributes. Bridges the self-describing qualified `_type`
 * (`timeline[column].points`) and the legacy `data-renderer` shim (kind in
 * `attributes.rendererId` / `attributes.type`), so consumers derive the
 * mark kind without branching on the wire spelling.
 *
 * See `sheets/.docs/content/rows-per-type.md`.
 */

import type { Option } from "../core/option.ts";
import { Json, type JsonObject } from "../core/json.ts";
import { parseRendererId } from "../rows/slot.ts";

/**
 * Resolve the renderer KIND (`points`, `event`, `timeline-legend`, …) for a
 * mark row. Resolution order:
 *   1. slot-qualified `_type` (`timeline[column].points` → `points`);
 *   2. legacy `attributes.rendererId` when a qualified id → its kind;
 *   3. legacy `attributes.type` when a string → itself.
 * Returns null when none apply.
 */
export function rendererSlotKindOf(
  wireType: string,
  attributes: Option<JsonObject>,
): Option<string> {
  const fromType = parseRendererId(wireType);
  if (fromType != null) return fromType.kind;

  const rendererId = Json.string(attributes, "rendererId");
  if (rendererId != null) {
    const parsed = parseRendererId(rendererId);
    if (parsed != null) return parsed.kind;
  }
  const type = Json.string(attributes, "type");
  if (type != null) return type;

  return null;
}

/**
 * True when `ty` is a renderer mark's wire type — the legacy `data-renderer`
 * shim or any slot-qualified renderer id.
 */
export function isRendererWireType(ty: string): boolean {
  return ty === "data-renderer" || parseRendererId(ty) != null;
}
